import { useEffect } from "react";

/**
 * Ask the browser to confirm before leaving with unsaved work.
 *
 * The in-app close paths already guard themselves — a review dialog asks before
 * discarding. Nothing guarded the browser's own exits: a refresh, a closed tab, a
 * typed URL. On a transcription screen that is the expensive one, because the work
 * is forty fields read off a bad scan and there is nothing to recover it from.
 *
 * The message is the browser's, not ours. Every current browser ignores whatever
 * string a page supplies here and shows its own wording, so passing one would
 * only suggest we control something we do not.
 *
 * Registered only while `unsaved` is true. A listener that is always attached
 * makes every navigation away from the page prompt, which trains people to click
 * through the one that matters.
 */
export function useWarnBeforeUnload(unsaved: boolean): void {
  useEffect(() => {
    if (!unsaved) return;

    const warn = (event: BeforeUnloadEvent) => {
      // Both, deliberately: `preventDefault` is the standard, and assigning
      // `returnValue` is what older WebKit still reads.
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);
}
